import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useInjectReducer, useInjectSaga } from "redux-injectors";
import { Card, Typography, Spin, Alert } from "antd";
import {
  ExperimentOutlined,
  CheckCircleOutlined,
  TagsOutlined,
} from "@ant-design/icons";
import { reducer, sliceKey, actions } from "./slice";
import { overallComplianceSaga } from "./saga";
import * as selectors from "./selectors";

const { Title, Text } = Typography;

const YellowWarningIcon = (props) => (
  <img
    src={require("./yellow_warning.svg").default}
    alt="warning"
    style={{ width: 26, height: 26, ...props.style }}
  />
);
const RedWarningIcon = (props) => (
  <img
    src={require("./red_warning.svg").default}
    alt="red_warning"
    style={{ width: 26, height: 26, ...props.style }}
  />
);
const OverallCompliance = ({ filters = {} }) => {
  useInjectReducer({ key: sliceKey, reducer });
  useInjectSaga({ key: sliceKey, saga: overallComplianceSaga });

  const dispatch = useDispatch();
  const loading = useSelector(selectors.selectLoading);
  const error = useSelector(selectors.selectError);
  const complianceData = useSelector(selectors.selectComplianceData);

  const filterString = JSON.stringify(filters);

  useEffect(() => {
    dispatch(actions.fetchCompliance({ filtersString: filterString }));
  }, [filterString, dispatch]);

  const getComplianceCards = () => {
    if (!complianceData || Object.keys(complianceData).length === 0) {
      return [];
    }
    const cards = [];
    cards.push({
      label: "Total Samples Tested",
      count: complianceData.totalSamplesTested?.toLocaleString() || "0",
      color: "#dcebfa",
      icon: <ExperimentOutlined style={{ fontSize: 24, color: "#4e76e6" }} />,
    });

    cards.push({
      label: "Compliant Samples",
      count: complianceData.compliantSamples?.count?.toLocaleString() || "0",
      percentage: `${complianceData.compliantSamples?.percentage || 0}%`,
      color: "#ddf4e7",
      icon: <CheckCircleOutlined style={{ fontSize: 24, color: "#4e9b75" }} />,
      progressColor: "#3a7f5e",
      progressWidth: `${complianceData.compliantSamples?.percentage || 0}%`,
    });

    cards.push({
      label: "Unsafe Samples",
      count: complianceData.unsafeSamples?.count?.toLocaleString() || "0",
      percentage: `${complianceData.unsafeSamples?.percentage || 0}%`,
      color: "#f8dedf",
      icon: <RedWarningIcon style={{ fontSize: 24 }} />,
      progressColor: "#ac332c",
      progressWidth: `${complianceData.unsafeSamples?.percentage || 0}%`,
    });

    cards.push({
      label: "Sub-standard Samples",
      count: complianceData.subStandardSamples?.count?.toLocaleString() || "0",
      percentage: `${complianceData.subStandardSamples?.percentage || 0}%`,
      color: "#fbefd7",
      icon: <YellowWarningIcon style={{ fontSize: 26 }} />,
      progressColor: "#ad6b2a",
      progressWidth: `${complianceData.subStandardSamples?.percentage || 0}%`,
    });

    cards.push({
      label: "Mis-Labelled Samples",
      count: complianceData.misLabelledSamples?.count?.toLocaleString() || "0",
      percentage: `${complianceData.misLabelledSamples?.percentage || 0}%`,
      color: "#ede2fc",
      icon: <TagsOutlined style={{ fontSize: 24, color: "#934ce4" }} />,
      progressColor: "#7535c2",
      progressWidth: `${complianceData.misLabelledSamples?.percentage || 0}%`,
    });

    return cards;
  };

  const complianceCards = getComplianceCards();

  // Loading state
  if (loading) {
    return (
      <div
        style={{
          fontFamily:
            "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          marginTop: 16,
        }}
      >
        {/* Header */}
        <div
          style={{
            backgroundColor: "white",
            padding: "24px 32px",
            borderRadius: "16px 16px 0 0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
          }}
        >
          <h2
            style={{
              margin: "0",
              fontSize: "16px",
              fontWeight: "500",
              color: "#1a1a1a",
            }}
          >
            Compliance Distribution
          </h2>
        </div>
        <div
          style={{
            backgroundColor: "white",
            padding: "40px 24px",
            borderRadius: "0 0 16px 16px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
            marginTop: "-1px",
          }}
        >
          <Spin size="large" />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div
        style={{
          fontFamily:
            "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          marginTop: 16,
        }}
      >
        {/* Header */}
        <div
          style={{
            backgroundColor: "white",
            padding: "24px 32px",
            borderRadius: "16px 16px 0 0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
            marginBottom: "0",
          }}
        >
          <div>
            <h2
              style={{
                margin: "0",
                fontSize: "16px",
                fontWeight: "500",
                color: "#1a1a1a",
                lineHeight: "1.2",
              }}
            >
              Compliance Distribution
            </h2>
          </div>
        </div>
        {/* Error Content */}
        <div
          style={{
            backgroundColor: "#f8f9fa",
            padding: "32px 24px",
            borderRadius: "0 0 8px 8px",
            marginTop: "-1px",
          }}
        >
          <Alert
            message="Error Loading Compliance Data"
            description={
              error?.message ||
              "Failed to fetch compliance data. Please try again."
            }
            type="error"
            showIcon
          />
        </div>
      </div>
    );
  }

  // No data state
  if (complianceCards.length === 0) {
    return (
      <div
        style={{
          fontFamily:
            "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          marginTop: 16,
        }}
      >
        {/* Header */}
        <div
          style={{
            backgroundColor: "white",
            padding: "24px 32px",
            borderRadius: "16px 16px 0 0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
            marginBottom: "0",
          }}
        >
          <div>
            <h2
              style={{
                margin: "0",
                fontSize: "16px",
                fontWeight: "500",
                color: "#1a1a1a",
                lineHeight: "1.2",
              }}
            >
              Compliance Distribution
            </h2>
          </div>
        </div>
        {/* No Data Content */}
        <div
          style={{
            backgroundColor: "#f8f9fa",
            padding: "32px 24px",
            borderRadius: "0 0 8px 8px",
            marginTop: "-1px",
          }}
        >
          <Alert
            message="No Data Available"
            description="No compliance data found for the selected filters."
            type="info"
            showIcon
          />
        </div>
      </div>
    );
  }

  // Main render with data
  return (
    <div
      style={{
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        marginTop: 16,
      }}
    >
      {/* Header */}
      <div
        style={{
          backgroundColor: "white",
          padding: "24px 32px",
          borderRadius: "16px 16px 0 0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
          marginBottom: "0",
        }}
      >
        {/* Left side - Title */}
        <div>
          <h2
            style={{
              margin: "0",
              fontSize: "16px",
              fontWeight: "500",
              color: "#1a1a1a",
              lineHeight: "1.2",
            }}
          >
            Compliance Distribution
          </h2>
        </div>
      </div>

      <div
        style={{
          backgroundColor: "#f8f9fa",
          padding: "32px 24px",
          borderRadius: "0 0 8px 8px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "16px",
          marginTop: "-1px",
        }}
      >
        {complianceCards.map((item, index) => (
          <Card
            key={index}
            style={{
              backgroundColor: item.color,
              borderRadius: "12px",
              textAlign: "center",
              border: "none",
              position: "relative",
              cursor: "pointer",
              transition: "all 0.3s ease",
              height: "120px",
              width: "150px",
              minWidth: "150px",
              flex: "1",
              maxWidth: "220px",
            }}
            bodyStyle={{
              padding: "16px",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              position: "relative",
            }}
            hoverable={false}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-4px)";
              e.currentTarget.style.boxShadow = "0 8px 25px rgba(0,0,0,0.15)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            {/* Percentage badge - top right */}
            {item.percentage && (
              <div
                style={{
                  position: "absolute",
                  top: "8px",
                  right: "8px",
                  backgroundColor: "rgba(255,255,255,0.9)",
                  color: "#333",
                  padding: "2px 6px",
                  borderRadius: "8px",
                  fontSize: "10px",
                  fontWeight: "600",
                  zIndex: 1,
                }}
              >
                {item.percentage}
              </div>
            )}

            {/* Icon - Top Left */}
            <div
              style={{
                position: "absolute",
                top: "12px",
                left: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1,
              }}
            >
              {item.icon}
            </div>

            {/* Main Content - Centered */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "4px",
                marginTop: "8px",
              }}
            >
              {/* Count */}
              <Title
                level={2}
                style={{
                  margin: "0",
                  fontSize: "22px",
                  fontWeight: "700",
                  color: "#1a1a1a",
                  lineHeight: "1",
                }}
              >
                {item.count}
              </Title>

              {/* Label */}
              <Text
                style={{
                  fontSize: "12px",
                  color: "#666",
                  fontWeight: "500",
                  textAlign: "center",
                  lineHeight: "1.2",
                  maxWidth: "160px",
                }}
              >
                {item.label}
              </Text>
            </div>

            {/* Progress Bar - Bottom */}
            {item.progressColor && (
              <div
                style={{
                  position: "absolute",
                  bottom: "12px",
                  left: "12px",
                  right: "12px",
                  height: "3px",
                  backgroundColor: "rgba(255,255,255,0.5)",
                  borderRadius: "2px",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: item.progressWidth,
                    height: "100%",
                    backgroundColor: item.progressColor,
                    borderRadius: "2px",
                    transition: "width 0.3s ease",
                  }}
                />
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
};
export default OverallCompliance;
